import { isPregameActionType, type PregameActionType } from "./actions.js";
import {
  createSpecialBeerDuty,
  isScheduleField,
  isScheduleTimeSlot,
  isSpecialBeerDutyKind,
  type BeerDuty,
  type ScheduleField,
  type ScheduleTimeSlot,
} from "./schedule.js";
import { generateDefense, type DefenseInning } from "./defenseGenerator.js";
import { isDefensePosition, type DefensePosition } from "./defense.js";
import {
  createInitialGameState,
  emptyStats,
  type GameLineupEntry,
  type GameState,
} from "./gameReducer.js";

declare const structuredClone: <Value>(value: Value) => Value;

export type PregameActionErrorCode = "invalid-argument" | "failed-precondition";

export interface PregamePlayer {
  playerId: string;
  name: string;
  number?: number | null;
  positionPreferences: readonly DefensePosition[];
  active: boolean;
  revision: number;
}

export interface PregameClaim {
  playerId: string;
  uid?: string;
}

export interface PregameDocument {
  opponentName: string;
  isHome: boolean;
  openInning: boolean;
  activePlayerIds: string[];
  lineupPlayerIds: string[];
  gamePitcherId: string | null;
  defense: DefenseInning<string>[] | null;
  selectedScheduleId?: string | null;
  scheduledDate?: string | null;
  timeSlot?: ScheduleTimeSlot | null;
  field?: ScheduleField | null;
  beerDuty?: BeerDuty | null;
}

export interface PregameActionContext {
  jerseyHolderPlayerId?: string | null;
  players: readonly PregamePlayer[];
  claims?: readonly PregameClaim[];
  activeGamePlayerIds?: readonly string[];
}

export interface PregameAction {
  type?: unknown;
  payload?: unknown;
}

export interface PregameTeamEffects {
  jerseyHolderPlayerId?: string | null;
  teamName?: string;
}

export interface PlayerCreateData {
  name: string;
  number: number | null;
  positionPreferences: DefensePosition[];
  active: true;
}

export interface PlayerUpdatePatch {
  name?: string;
  number?: number | null;
  positionPreferences?: DefensePosition[];
  active?: boolean;
}

export type PregamePlayerEffect =
  | { op: "create"; playerId: string; data: PlayerCreateData }
  | { op: "update"; playerId: string; patch: PlayerUpdatePatch }
  | { op: "delete"; playerId: string };

export interface PregameAudit {
  type: PregameActionType;
  summary: string;
  payload: Record<string, unknown>;
}

export interface PregameActionResult {
  nextPregame: PregameDocument;
  teamEffects: PregameTeamEffects;
  playerEffects: PregamePlayerEffect[];
  audit: PregameAudit;
}

export interface GameFromPregame {
  opponentName: string;
  isHome: boolean;
  openInning: boolean;
  scheduleId: string | null;
  scheduledDate: string | null;
  timeSlot: ScheduleTimeSlot | null;
  field: ScheduleField | null;
  beerDuty: BeerDuty | null;
  lineup: GameLineupEntry[];
  defense: DefenseInning<string>[];
  startingJerseyHolderPlayerId: string | null;
  state: GameState;
}

type PregamePayload = Record<string, unknown>;
type PregameAuditFields = Omit<PregameAudit, "type">;
type PregameActionHandler = (
  pregame: PregameDocument,
  context: PregameActionContext,
  payload: PregamePayload,
  teamEffects: PregameTeamEffects,
  playerEffects: PregamePlayerEffect[],
) => PregameAuditFields;

export class PregameActionError extends Error {
  code: PregameActionErrorCode;
  details?: Record<string, unknown>;

  constructor(
    code: PregameActionErrorCode,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "PregameActionError";
    this.code = code;
    if (details !== undefined) this.details = details;
  }
}

const invalid = (message: string, details?: Record<string, unknown>) =>
  new PregameActionError("invalid-argument", message, details);
const failedPre = (message: string, details?: Record<string, unknown>) =>
  new PregameActionError("failed-precondition", message, details);

export function createInitialPregame(): PregameDocument {
  return {
    opponentName: "Opponent",
    isHome: false,
    openInning: false,
    activePlayerIds: [],
    lineupPlayerIds: [],
    gamePitcherId: null,
    defense: null,
  };
}

function findPlayer(
  ctx: PregameActionContext,
  playerId: string,
): PregamePlayer {
  const player = ctx.players.find(
    (candidate) => candidate.playerId === playerId,
  );
  if (!player)
    throw failedPre(`Unknown player: ${playerId}`, {
      reason: "unknown-player",
      playerId,
    });
  return player;
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== "string") throw invalid(`${field} must be a string`);
  return value;
}

function requireBoolean(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") throw invalid(`${field} must be a boolean`);
  return value;
}

function requireName(value: unknown, field = "name"): string {
  const name = requireString(value, field).trim();
  if (!name) throw invalid(`${field} must not be empty`);
  if (name.length > 80) throw invalid(`${field} is too long`);
  return name;
}

function requireNumber(value: unknown): number | null {
  if (value === null) return null;
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw invalid("number must be a non-negative integer or null");
  }
  return value;
}

function requirePositionPreferences(value: unknown): DefensePosition[] {
  if (!Array.isArray(value))
    throw invalid("positionPreferences must be an array");
  const seen = new Set<DefensePosition>();
  for (const pos of value) {
    if (!isDefensePosition(pos))
      throw invalid(`Unknown position: ${String(pos)}`);
    if (seen.has(pos)) throw invalid(`Duplicate position preference: ${pos}`);
    seen.add(pos);
  }
  return [...value];
}

function requirePlayerRevision(
  player: PregamePlayer,
  expectedPlayerRevision: unknown,
): void {
  if (
    typeof expectedPlayerRevision !== "number" ||
    !Number.isInteger(expectedPlayerRevision)
  ) {
    throw invalid("expectedPlayerRevision must be an integer");
  }
  if (expectedPlayerRevision !== player.revision) {
    throw failedPre(`Player ${player.name} has changed`, {
      reason: "player-revision-mismatch",
      playerId: player.playerId,
      latestRevision: player.revision,
    });
  }
}

function removeFromPregame(pre: PregameDocument, playerId: string): void {
  pre.activePlayerIds = pre.activePlayerIds.filter((id) => id !== playerId);
  pre.lineupPlayerIds = pre.lineupPlayerIds.filter((id) => id !== playerId);
  if (pre.gamePitcherId === playerId) pre.gamePitcherId = null;
}

function setAttendance(
  pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  const playerId = requireString(payload.playerId, "playerId");
  const isActive = requireBoolean(payload.isActive, "isActive");
  const player = findPlayer(ctx, playerId);
  if (isActive) {
    if (!pre.activePlayerIds.includes(playerId))
      pre.activePlayerIds.push(playerId);
    if (!pre.lineupPlayerIds.includes(playerId))
      pre.lineupPlayerIds.push(playerId);
  } else {
    removeFromPregame(pre, playerId);
  }
  return {
    summary: `${player.name} marked ${isActive ? "in" : "out"}`,
    payload: { playerId, isActive },
  };
}

function setOpponentName(
  pre: PregameDocument,
  _ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  const value = requireName(payload.value, "opponent name");
  pre.opponentName = value;
  return { summary: `Opponent set to ${value}`, payload: { value } };
}

function setHomeTeam(
  pre: PregameDocument,
  _ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  const value = requireBoolean(payload.value, "value");
  pre.isHome = value;
  return {
    summary: value ? "We are the home team" : "We are the away team",
    payload: { value },
  };
}

function setScheduleDetails(
  pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  if (payload.scheduledDate !== null && payload.scheduledDate !== undefined) {
    const value = requireString(payload.scheduledDate, "scheduledDate").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
      throw invalid("scheduledDate must use YYYY-MM-DD");
    pre.scheduledDate = value;
  } else if (payload.scheduledDate === null) {
    pre.scheduledDate = null;
  }
  if (payload.timeSlot !== null && payload.timeSlot !== undefined) {
    if (!isScheduleTimeSlot(payload.timeSlot))
      throw invalid("timeSlot must be early or late");
    pre.timeSlot = payload.timeSlot;
  } else if (payload.timeSlot === null) {
    pre.timeSlot = null;
  }
  if (payload.field !== null && payload.field !== undefined) {
    if (!isScheduleField(payload.field)) {
      throw invalid("field is not supported");
    }
    pre.field = payload.field;
  } else if (payload.field === null) {
    pre.field = null;
  }
  if (payload.beerDuty !== null && payload.beerDuty !== undefined) {
    const duty = payload.beerDuty;
    const dutyRecord =
      duty && typeof duty === "object"
        ? (duty as Record<string, unknown>)
        : null;
    if (isSpecialBeerDutyKind(dutyRecord?.kind)) {
      pre.beerDuty = createSpecialBeerDuty(dutyRecord.kind);
    } else if (
      dutyRecord?.kind === "player" &&
      typeof dutyRecord.playerId === "string"
    ) {
      const player = findPlayer(ctx, dutyRecord.playerId);
      if (!player.active)
        throw failedPre(`${player.name} is deactivated`, {
          reason: "beer-player-unavailable",
        });
      pre.beerDuty = {
        kind: "player",
        playerId: player.playerId,
        nameSnapshot: player.name,
      };
    } else {
      throw invalid("beerDuty is invalid");
    }
  } else if (payload.beerDuty === null) {
    pre.beerDuty = null;
  }
  return {
    summary: "Schedule details updated",
    payload: {
      scheduledDate: pre.scheduledDate ?? null,
      timeSlot: pre.timeSlot ?? null,
      field: pre.field ?? null,
      beerDuty: pre.beerDuty ?? null,
    },
  };
}

function setOpenInning(
  pre: PregameDocument,
  _ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  const value = requireBoolean(payload.value, "value");
  pre.openInning = value;
  return {
    summary: value ? "Open inning declared" : "Open inning rescinded",
    payload: { value },
  };
}

function setPitcher(
  pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  if (payload.playerId === null) {
    pre.gamePitcherId = null;
    return { summary: "Pitcher cleared", payload: { playerId: null } };
  }
  const playerId = requireString(payload.playerId, "playerId");
  const player = findPlayer(ctx, playerId);
  if (!pre.activePlayerIds.includes(playerId)) {
    throw failedPre(`${player.name} is not marked in`, {
      reason: "player-not-attending",
      playerId,
    });
  }
  pre.gamePitcherId = playerId;
  return { summary: `${player.name} set as pitcher`, payload: { playerId } };
}

function setJerseyHolder(
  _pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
  teamEffects: PregameTeamEffects,
): PregameAuditFields {
  const fromPlayerId = ctx.jerseyHolderPlayerId ?? null;
  if (payload.playerId === null) {
    teamEffects.jerseyHolderPlayerId = null;
    return {
      summary: "Strikeout jersey holder cleared",
      payload: { fromPlayerId, toPlayerId: null },
    };
  }
  const playerId = requireString(payload.playerId, "playerId");
  const player = findPlayer(ctx, playerId);
  if (!player.active) {
    throw failedPre(`${player.name} is deactivated`, {
      reason: "player-deactivated",
      playerId,
    });
  }
  teamEffects.jerseyHolderPlayerId = playerId;
  return {
    summary: `Strikeout jersey holder set to ${player.name}`,
    payload: { fromPlayerId, toPlayerId: playerId },
  };
}

function setTeamName(
  _pre: PregameDocument,
  _ctx: PregameActionContext,
  payload: PregamePayload,
  teamEffects: PregameTeamEffects,
): PregameAuditFields {
  const name = requireString(payload.name, "name").trim();
  if (!name || name.length > 80)
    throw invalid("name must be 1-80 characters", { reason: "invalid-name" });
  teamEffects.teamName = name;
  return { summary: `Team renamed to ${name}`, payload: { name } };
}

function reorderLineup(
  pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  const playerIds = payload.playerIds;
  if (
    !Array.isArray(playerIds) ||
    playerIds.some((id) => typeof id !== "string")
  ) {
    throw invalid("playerIds must be an array of strings");
  }
  if (new Set(playerIds).size !== playerIds.length)
    throw invalid("playerIds must be unique");
  for (const id of playerIds) {
    findPlayer(ctx, id);
    if (!pre.activePlayerIds.includes(id)) {
      const player = findPlayer(ctx, id);
      throw failedPre(`${player.name} is not marked in`, {
        reason: "player-not-attending",
        playerId: id,
      });
    }
  }
  pre.lineupPlayerIds = [...playerIds];
  return {
    summary: "Batting order updated",
    payload: { playerIds: [...playerIds] },
  };
}

function setDefenseAssignment(
  pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
): PregameAuditFields {
  if (!pre.defense) {
    throw failedPre("Defense has not been generated", {
      reason: "defense-not-generated",
    });
  }
  const inning = payload.inning;
  const position = payload.position;
  const playerId = payload.playerId ?? null;
  const expectedOccupantId = payload.expectedOccupantId ?? null;
  if (
    typeof inning !== "number" ||
    !Number.isInteger(inning) ||
    inning < 0 ||
    inning >= pre.defense.length
  ) {
    throw invalid(`Invalid inning: ${String(inning)}`);
  }
  if (!isDefensePosition(position))
    throw invalid(`Unknown position: ${String(position)}`);
  if (playerId !== null && typeof playerId !== "string")
    throw invalid("playerId must be a string or null");

  const inn = pre.defense[inning];
  if (!inn) throw invalid(`Invalid inning: ${inning}`);
  const current = inn.assignment[position] ?? null;
  if (expectedOccupantId !== current) {
    throw failedPre("Defense cell changed by someone else", {
      reason: "defense-cell-mismatch",
      inning,
      position,
      actualOccupantId: current,
    });
  }

  let name = null;
  if (playerId !== null) {
    const player = findPlayer(ctx, playerId);
    if (!pre.activePlayerIds.includes(playerId)) {
      throw failedPre(`${player.name} is not marked in`, {
        reason: "player-not-attending",
        playerId,
      });
    }
    name = player.name;
    for (const pos of Object.keys(inn.assignment) as DefensePosition[]) {
      if (inn.assignment[pos] === playerId) delete inn.assignment[pos];
    }
    inn.assignment[position] = playerId;
  } else {
    delete inn.assignment[position];
  }

  const onField = Object.values(inn.assignment);
  inn.sitters = pre.activePlayerIds.filter((id) => !onField.includes(id));
  inn.oop = (Object.entries(inn.assignment) as Array<[DefensePosition, string]>)
    .filter(([pos, id]) => {
      const player = ctx.players.find((candidate) => candidate.playerId === id);
      return !player?.positionPreferences?.includes(pos);
    })
    .map(([, id]) => id);

  return {
    summary: playerId
      ? `${name} at ${position} (inning ${inning + 1})`
      : `${position} cleared (inning ${inning + 1})`,
    payload: { inning, position, playerId },
  };
}

function regenerateDefense(
  pre: PregameDocument,
  ctx: PregameActionContext,
): PregameAuditFields {
  if (!pre.activePlayerIds.length) {
    throw failedPre("No players are marked in", {
      reason: "no-active-players",
    });
  }
  const activePlayers = pre.activePlayerIds.map((id) => {
    const pl = findPlayer(ctx, id);
    return {
      playerId: pl.playerId,
      name: pl.name,
      positionPreferences: pl.positionPreferences ?? [],
    };
  });
  pre.defense = generateDefense(activePlayers, pre.gamePitcherId);
  return {
    summary: "Defense regenerated",
    payload: { pitcherId: pre.gamePitcherId },
  };
}

function addPlayer(
  _pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
  _teamEffects: PregameTeamEffects,
  playerEffects: PregamePlayerEffect[],
): PregameAuditFields {
  const playerId = requireString(payload.playerId, "playerId").trim();
  if (!playerId) throw invalid("playerId must not be empty");
  if (ctx.players.some((player) => player.playerId === playerId)) {
    throw failedPre("Player already exists", {
      reason: "player-exists",
      playerId,
    });
  }
  const name = requireName(payload.name);
  const number = requireNumber(payload.number ?? null);
  const positionPreferences = requirePositionPreferences(
    payload.positionPreferences ?? [],
  );
  playerEffects.push({
    op: "create",
    playerId,
    data: { name, number, positionPreferences, active: true },
  });
  return { summary: `${name} added to roster`, payload: { playerId, name } };
}

function updatePlayer(
  pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
  _teamEffects: PregameTeamEffects,
  playerEffects: PregamePlayerEffect[],
): PregameAuditFields {
  const playerId = requireString(payload.playerId, "playerId");
  const player = findPlayer(ctx, playerId);
  requirePlayerRevision(player, payload.expectedPlayerRevision);

  const patch: PlayerUpdatePatch = {};
  if (payload.name !== undefined) patch.name = requireName(payload.name);
  if (payload.number !== undefined)
    patch.number = requireNumber(payload.number);
  if (payload.positionPreferences !== undefined) {
    patch.positionPreferences = requirePositionPreferences(
      payload.positionPreferences,
    );
  }
  if (payload.active !== undefined)
    patch.active = requireBoolean(payload.active, "active");
  if (!Object.keys(patch).length) throw invalid("No fields to update");

  if (patch.active === false) removeFromPregame(pre, playerId);

  playerEffects.push({ op: "update", playerId, patch });
  const keys = Object.keys(patch);
  let summary;
  if (keys.length === 1 && keys[0] === "active") {
    summary = `${player.name} ${patch.active ? "reactivated" : "deactivated"}`;
  } else {
    summary = `${player.name} updated`;
  }
  return { summary, payload: { playerId, patch } };
}

function removePlayer(
  pre: PregameDocument,
  ctx: PregameActionContext,
  payload: PregamePayload,
  _teamEffects: PregameTeamEffects,
  playerEffects: PregamePlayerEffect[],
): PregameAuditFields {
  const playerId = requireString(payload.playerId, "playerId");
  const player = findPlayer(ctx, playerId);
  requirePlayerRevision(player, payload.expectedPlayerRevision);

  if ((ctx.claims ?? []).some((claim) => claim.playerId === playerId)) {
    throw failedPre(`${player.name} is claimed by a member`, {
      reason: "player-claimed",
      playerId,
    });
  }
  if (ctx.jerseyHolderPlayerId === playerId) {
    throw failedPre(`${player.name} holds the strikeout jersey`, {
      reason: "player-is-jersey-holder",
      playerId,
    });
  }
  if ((ctx.activeGamePlayerIds ?? []).includes(playerId)) {
    throw failedPre(`${player.name} is referenced by the active game`, {
      reason: "player-in-active-game",
      playerId,
    });
  }

  removeFromPregame(pre, playerId);
  playerEffects.push({ op: "delete", playerId });
  return {
    summary: `${player.name} removed from roster`,
    payload: { playerId },
  };
}

const ACTIONS: Partial<Record<PregameActionType, PregameActionHandler>> = {
  setAttendance,
  setOpponentName,
  setHomeTeam,
  setScheduleDetails,
  setOpenInning,
  setPitcher,
  setJerseyHolder,
  setTeamName,
  reorderLineup,
  setDefenseAssignment,
  regenerateDefense,
  addPlayer,
  updatePlayer,
  removePlayer,
};

export function reducePregameAction(
  pregame: PregameDocument,
  context: PregameActionContext,
  action: PregameAction | null | undefined,
): PregameActionResult {
  if (!action || typeof action.type !== "string")
    throw invalid("action.type is required");
  if (!isPregameActionType(action.type)) {
    throw invalid(`Unknown action type: ${action.type}`);
  }
  const handler = ACTIONS[action.type];
  if (!handler) throw invalid(`Unknown action type: ${action.type}`);

  const nextPregame = structuredClone(pregame);
  const teamEffects: PregameTeamEffects = {};
  const playerEffects: PregamePlayerEffect[] = [];
  const payload =
    action.payload && typeof action.payload === "object"
      ? (action.payload as PregamePayload)
      : {};
  const audit = handler(
    nextPregame,
    context,
    payload,
    teamEffects,
    playerEffects,
  );

  return {
    nextPregame,
    teamEffects,
    playerEffects,
    audit: { type: action.type, ...audit },
  };
}

export function buildGameFromPregame(
  pregame: PregameDocument,
  context: Pick<PregameActionContext, "jerseyHolderPlayerId" | "players">,
): GameFromPregame {
  if (!pregame.lineupPlayerIds.length) {
    throw failedPre("Lineup is empty", { reason: "lineup-empty" });
  }
  const lineup = pregame.lineupPlayerIds.map((id) => {
    const pl = findPlayer(context, id);
    return { playerId: pl.playerId, name: pl.name, number: pl.number ?? null };
  });
  const state = createInitialGameState(pregame.isHome);
  for (const entry of lineup)
    state.statsByPlayerId[entry.playerId] = emptyStats();
  return {
    opponentName: pregame.opponentName,
    isHome: pregame.isHome,
    openInning: !!pregame.openInning,
    scheduleId: pregame.selectedScheduleId ?? null,
    scheduledDate: pregame.scheduledDate ?? null,
    timeSlot: pregame.timeSlot ?? null,
    field: pregame.field ?? null,
    beerDuty: structuredClone(pregame.beerDuty ?? null),
    lineup,
    defense: structuredClone(pregame.defense ?? []),
    startingJerseyHolderPlayerId: context.jerseyHolderPlayerId ?? null,
    state,
  };
}
